import { t } from '@glory/i18n';
import { ChevronDown, Plus, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useEffectEvent, useState } from 'react';

import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../components/popover.tsx';
import { cn } from '../lib/cn.ts';
import { matchesWords } from '../lib/text-match.ts';
import type { ChoiceOption } from './choice-fields.tsx';
import {
  Field,
  FieldAction,
  type FieldPart,
  type FieldSize,
  fieldBoxClass,
  inputClass,
  splitField,
  useFieldControl,
  useFieldState,
} from './field.tsx';
import { ListMessage, ListPanel, OptionRow } from './listbox.tsx';

/** Больше вариантов — над списком строка поиска. */
export const SEARCH_FROM = 8;

interface TriggerState {
  readonly size?: FieldSize | undefined;
  readonly variant?: 'default' | 'cell' | undefined;
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly invalid?: boolean | undefined;
  readonly id?: string | undefined;
  readonly className?: string | undefined;
  readonly 'aria-label'?: string | undefined;
  /** Открыт при показе: истории и тесты. */
  readonly defaultOpen?: boolean | undefined;
}

/** Состояние поля выбора: из `Field` или из свойств (в ячейке таблицы, в фильтрах). */
function useTriggerState(props: TriggerState) {
  const field = useFieldState();
  const { disabled: fieldDisabled, readOnly: fieldReadOnly, ...control } = useFieldControl({ id: props.id });
  const disabled = props.disabled ?? fieldDisabled ?? false;
  const readOnly = props.readOnly ?? fieldReadOnly ?? false;
  const invalid = props.invalid ?? field?.invalid ?? false;
  return { control, disabled, readOnly, invalid, locked: disabled || readOnly };
}

const isPrintable = (e: KeyboardEvent) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

/** Крестик «Очистить» — поверх поля, перед стрелкой (кнопку нельзя вложить в кнопку-поле). */
const clearClass = 'absolute top-1/2 right-8 flex -translate-y-1/2 [&_svg]:size-4';

/**
 * Панель списка выбора: шириной с поле и не выше свободного места на экране — поиск остаётся сверху, список
 * сжимается и прокручивается, а не вылезает за край окна или диалога.
 */
const LIST_POPOVER = 'flex w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0';

export interface SelectProps<V extends string> extends TriggerState {
  readonly contentClassName?: string;
  readonly value: V | null;
  readonly onValueChange: (value: V | null) => void;
  readonly options: readonly ChoiceOption<V>[];
  readonly placeholder?: string;
  /** Можно сбросить выбор крестиком. */
  readonly clearable?: boolean;
  /** Строка поиска; по умолчанию — когда вариантов больше семи. */
  readonly searchable?: boolean;
}

/**
 * Выбор одного варианта. До семи вариантов — список без поиска, больше — поиск по мере ввода
 * (можно начать печатать прямо на закрытом поле). Стрелки, Enter, Esc; Tab уходит дальше.
 */
export function Select<V extends string>({
  value,
  onValueChange,
  options,
  placeholder = t('fields.selectPlaceholder'),
  clearable = false,
  searchable = options.length >= SEARCH_FROM,
  size,
  variant,
  className,
  contentClassName,
  defaultOpen = false,
  'aria-label': ariaLabel,
  ...state
}: SelectProps<V>) {
  const { control, invalid, disabled, readOnly, locked } = useTriggerState(state);
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.value === value) ?? null;
  const visible = searchable
    ? options.filter((o) => matchesWords([o.label, o.description, ...(o.keywords ?? [])], query))
    : options;

  const changeOpen = (next: boolean) => {
    if (locked && next) return;
    setOpen(next);
    if (!next) setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverAnchor asChild>
        <div className={cn('relative w-full min-w-0', className)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={ariaLabel}
              aria-readonly={readOnly || undefined}
              {...control}
              disabled={disabled}
              className={fieldBoxClass({
                size,
                variant,
                invalid,
                disabled,
                readOnly,
                className: 'text-left',
              })}
              onClick={(e) => {
                if (readOnly) e.preventDefault();
              }}
              onKeyDown={(e) => {
                if (locked) return;
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  changeOpen(true);
                } else if (searchable && isPrintable(e)) {
                  e.preventDefault();
                  setQuery(e.key);
                  changeOpen(true);
                }
              }}
            >
              {selected?.icon ? (
                <span className="flex shrink-0 text-muted [&_svg]:size-4">{selected.icon}</span>
              ) : null}
              <span
                className={cn(
                  inputClass,
                  'truncate self-center',
                  !selected && 'text-muted',
                  clearable && selected && !locked && 'pr-7',
                )}
              >
                {selected?.label ?? placeholder}
              </span>
              {readOnly ? null : <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted" />}
            </button>
          </PopoverTrigger>
          {clearable && selected && !locked ? (
            <span className={clearClass}>
              <FieldAction
                label={t('ui.clear')}
                onClick={() => {
                  onValueChange(null);
                }}
              >
                <X />
              </FieldAction>
            </span>
          ) : null}
        </div>
      </PopoverAnchor>
      <PopoverContent className={cn(LIST_POPOVER, 'min-w-56', contentClassName)}>
        <ListPanel
          items={visible}
          getKey={(o) => o.value}
          isDisabled={(o) => o.disabled === true}
          isSelected={(o) => o.value === value}
          initialKey={value}
          label={ariaLabel ?? t('fields.selectOptions')}
          {...(searchable ? { search: { value: query, onChange: setQuery } } : {})}
          message={visible.length === 0 ? <ListMessage>{t('fields.selectEmpty')}</ListMessage> : undefined}
          onSelect={(o) => {
            onValueChange(o.value);
            changeOpen(false);
          }}
          renderItem={(o, row) => (
            <OptionRow
              label={o.label}
              description={o.description}
              icon={o.icon}
              disabled={o.disabled}
              active={row.active}
              selected={row.selected}
            />
          )}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface SelectFieldProps<V extends string>
  extends FieldPart, Omit<SelectProps<V>, keyof FieldPart> {}

export function SelectField<V extends string>(props: SelectFieldProps<V>) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <Select {...input} />
    </Field>
  );
}

export interface MultiSelectProps<V extends string> extends TriggerState {
  readonly value: readonly V[];
  readonly onValueChange: (value: V[]) => void;
  readonly options: readonly ChoiceOption<V>[];
  readonly placeholder?: string;
  readonly searchable?: boolean;
}

/**
 * Мультивыбор: выбранное — чипами в поле, список остаётся открытым, пока выбираете.
 * Backspace на поле убирает последний чип.
 */
export function MultiSelect<V extends string>({
  value,
  onValueChange,
  options,
  placeholder = t('fields.selectPlaceholder'),
  searchable = options.length >= SEARCH_FROM,
  size,
  variant,
  className,
  defaultOpen = false,
  'aria-label': ariaLabel,
  ...state
}: MultiSelectProps<V>) {
  const { control, invalid, disabled, readOnly, locked } = useTriggerState(state);
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const chosen = new Set<string>(value);
  // Чипы — в порядке вариантов, а не в порядке нажатий: так их проще сверять со списком.
  const selected = options.filter((o) => chosen.has(o.value));
  const visible = searchable
    ? options.filter((o) => matchesWords([o.label, o.description, ...(o.keywords ?? [])], query))
    : options;

  const changeOpen = (next: boolean) => {
    if (locked && next) return;
    setOpen(next);
    if (!next) setQuery('');
  };
  const toggle = (option: ChoiceOption<V>) => {
    onValueChange(
      chosen.has(option.value)
        ? value.filter((v) => v !== option.value)
        : options.filter((o) => chosen.has(o.value) || o.value === option.value).map((o) => o.value),
    );
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverAnchor asChild>
        <div
          className={fieldBoxClass({
            size,
            variant,
            invalid,
            disabled,
            readOnly,
            className: cn('h-auto flex-wrap gap-1 py-1', className),
          })}
          onClick={(e) => {
            if (e.target === e.currentTarget) changeOpen(true);
          }}
        >
          {selected.map((option) => (
            <span
              key={option.value}
              className="inline-flex max-w-full items-center gap-1 rounded-xs bg-surface-2 py-px pr-0.5 pl-1.5 text-small font-semibold"
            >
              <span className="truncate">{option.label}</span>
              {locked ? null : (
                <button
                  type="button"
                  // С клавиатуры чип убирает Backspace на поле — здесь только мышь.
                  tabIndex={-1}
                  aria-label={t('fields.selectRemove', { label: option.label })}
                  className="grid size-4 place-items-center rounded-xs text-muted hover:bg-surface hover:text-text"
                  onClick={() => {
                    toggle(option);
                  }}
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={ariaLabel}
              aria-readonly={readOnly || undefined}
              {...control}
              disabled={disabled}
              className={cn(
                inputClass,
                'flex min-h-6 min-w-16 items-center justify-between gap-1 text-left outline-none',
              )}
              onClick={(e) => {
                if (readOnly) e.preventDefault();
              }}
              onKeyDown={(e) => {
                if (locked) return;
                if (e.key === 'Backspace' && selected.length > 0) {
                  e.preventDefault();
                  const last = selected.at(-1);
                  if (last) toggle(last);
                } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  changeOpen(true);
                } else if (searchable && isPrintable(e)) {
                  e.preventDefault();
                  setQuery(e.key);
                  changeOpen(true);
                }
              }}
            >
              <span className="truncate text-muted">{selected.length === 0 ? placeholder : null}</span>
              {readOnly ? null : <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted" />}
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className={cn(LIST_POPOVER, 'min-w-56')}>
        <ListPanel
          items={visible}
          multiple
          getKey={(o) => o.value}
          isDisabled={(o) => o.disabled === true}
          isSelected={(o) => chosen.has(o.value)}
          label={ariaLabel ?? t('fields.selectOptions')}
          {...(searchable ? { search: { value: query, onChange: setQuery } } : {})}
          message={visible.length === 0 ? <ListMessage>{t('fields.selectEmpty')}</ListMessage> : undefined}
          onSelect={toggle}
          renderItem={(o, row) => (
            <OptionRow
              multiple
              label={o.label}
              description={o.description}
              icon={o.icon}
              disabled={o.disabled}
              active={row.active}
              selected={row.selected}
            />
          )}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface MultiSelectFieldProps<V extends string>
  extends FieldPart, Omit<MultiSelectProps<V>, keyof FieldPart> {}

export function MultiSelectField<V extends string>(props: MultiSelectFieldProps<V>) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <MultiSelect {...input} />
    </Field>
  );
}

/** Строка асинхронного списка: найденный объект или «+ Создать „…“». */
type AsyncRow<T> =
  { readonly kind: 'item'; readonly item: T } | { readonly kind: 'create'; readonly query: string };

export interface AsyncSelectProps<T> extends TriggerState {
  readonly value: T | null;
  readonly onValueChange: (value: T | null) => void;
  /** Поиск на сервере; `signal` отменяет устаревший запрос, пока пользователь печатает. */
  readonly loadOptions: (query: string, signal: AbortSignal) => Promise<readonly T[]>;
  readonly getKey: (item: T) => string;
  readonly getLabel: (item: T) => string;
  /** Пояснение и значок строки: специализация, статус, аватар. */
  readonly describe?: (item: T) => { readonly description?: ReactNode; readonly icon?: ReactNode };
  /** «+ Создать „…“» из поля: вернуть созданный объект. */
  readonly onCreate?: (query: string) => Promise<T>;
  /** Мини-карточка подсвеченного варианта справа от списка. */
  readonly renderPreview?: (item: T) => ReactNode;
  readonly placeholder?: string;
  readonly clearable?: boolean;
  /** Искать, когда введено столько символов; 0 — список сразу при открытии. */
  readonly minQueryLength?: number;
  readonly debounceMs?: number;
}

interface Loaded<T> {
  readonly query: string;
  readonly items: readonly T[];
  readonly failed: boolean;
}

/** Результаты поиска на сервере с паузой на ввод и отменой устаревших запросов. */
function useAsyncOptions<T>(
  load: AsyncSelectProps<T>['loadOptions'],
  query: string,
  minLength: number,
  debounceMs: number,
) {
  const [loaded, setLoaded] = useState<Loaded<T> | null>(null);
  const [attempt, setAttempt] = useState(0);
  const run = useEffectEvent((q: string, signal: AbortSignal) => load(q, signal));
  const ready = query.trim().length >= minLength;

  useEffect(() => {
    if (!ready) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      run(query, controller.signal).then(
        (items) => {
          if (!controller.signal.aborted) setLoaded({ query, items, failed: false });
        },
        () => {
          if (!controller.signal.aborted) setLoaded({ query, items: [], failed: true });
        },
      );
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, ready, debounceMs, attempt]);

  return {
    ready,
    // Пока не пришёл ответ на текущий запрос, показываем прошлые результаты со значком загрузки.
    loading: ready && loaded?.query !== query,
    items: ready ? (loaded?.items ?? []) : [],
    failed: ready && loaded?.query === query && loaded.failed,
    retry: () => {
      setAttempt((n) => n + 1);
    },
  };
}

/**
 * Асинхронный выбор: поиск на сервере по мере ввода, длинный список — виртуальный,
 * «+ Создать „…“», если такого ещё нет. Значение — объект целиком: подпись не нужно догружать.
 */
export function AsyncSelect<T>({
  value,
  onValueChange,
  loadOptions,
  getKey,
  getLabel,
  describe,
  onCreate,
  renderPreview,
  placeholder = t('fields.selectPlaceholder'),
  clearable = false,
  minQueryLength = 0,
  debounceMs = 200,
  size,
  variant,
  className,
  defaultOpen = false,
  'aria-label': ariaLabel,
  ...state
}: AsyncSelectProps<T>) {
  const { control, invalid, disabled, readOnly, locked } = useTriggerState(state);
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const changeOpen = (next: boolean) => {
    if (locked && next) return;
    setOpen(next);
    if (!next) setQuery('');
  };
  const icon = value === null ? undefined : describe?.(value).icon;

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverAnchor asChild>
        <div className={cn('relative w-full min-w-0', className)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={ariaLabel}
              aria-readonly={readOnly || undefined}
              {...control}
              disabled={disabled}
              className={fieldBoxClass({
                size,
                variant,
                invalid,
                disabled,
                readOnly,
                className: 'text-left',
              })}
              onClick={(e) => {
                if (readOnly) e.preventDefault();
              }}
              onKeyDown={(e) => {
                if (locked) return;
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  changeOpen(true);
                } else if (isPrintable(e)) {
                  e.preventDefault();
                  setQuery(e.key);
                  changeOpen(true);
                }
              }}
            >
              {icon ? <span className="flex shrink-0 text-muted [&_svg]:size-4">{icon}</span> : null}
              <span
                className={cn(
                  inputClass,
                  'truncate self-center',
                  value === null && 'text-muted',
                  clearable && value !== null && !locked && 'pr-7',
                )}
              >
                {value === null ? placeholder : getLabel(value)}
              </span>
              {readOnly ? null : <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted" />}
            </button>
          </PopoverTrigger>
          {clearable && value !== null && !locked ? (
            <span className={clearClass}>
              <FieldAction
                label={t('ui.clear')}
                onClick={() => {
                  onValueChange(null);
                }}
              >
                <X />
              </FieldAction>
            </span>
          ) : null}
        </div>
      </PopoverAnchor>
      <PopoverContent className={cn(LIST_POPOVER, 'min-w-64', renderPreview && 'sm:min-w-[36rem]')}>
        <AsyncPanel
          query={query}
          onQueryChange={setQuery}
          value={value}
          loadOptions={loadOptions}
          getKey={getKey}
          getLabel={getLabel}
          describe={describe}
          onCreate={onCreate}
          renderPreview={renderPreview}
          minQueryLength={minQueryLength}
          debounceMs={debounceMs}
          label={ariaLabel ?? t('fields.selectOptions')}
          onPick={(item) => {
            onValueChange(item);
            changeOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Содержимое панели асинхронного выбора: живёт, пока панель открыта. */
function AsyncPanel<T>({
  query,
  onQueryChange,
  value,
  loadOptions,
  getKey,
  getLabel,
  describe,
  onCreate,
  renderPreview,
  minQueryLength,
  debounceMs,
  label,
  onPick,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  value: T | null;
  loadOptions: AsyncSelectProps<T>['loadOptions'];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  describe: AsyncSelectProps<T>['describe'];
  onCreate: AsyncSelectProps<T>['onCreate'];
  renderPreview: AsyncSelectProps<T>['renderPreview'];
  minQueryLength: number;
  debounceMs: number;
  label: string;
  onPick: (item: T) => void;
}) {
  const results = useAsyncOptions(loadOptions, query, minQueryLength, debounceMs);
  const [creating, setCreating] = useState(false);
  const [createFailed, setCreateFailed] = useState(false);
  const typed = query.trim();
  const exists = results.items.some((item) => getLabel(item).toLowerCase() === typed.toLowerCase());
  const rows: AsyncRow<T>[] = [
    ...results.items.map((item) => ({ kind: 'item' as const, item })),
    ...(onCreate && typed !== '' && !exists && !results.loading
      ? [{ kind: 'create' as const, query: typed }]
      : []),
  ];
  const selectedKey = value === null ? null : getKey(value);

  const create = async (text: string) => {
    if (!onCreate || creating) return;
    setCreating(true);
    setCreateFailed(false);
    try {
      onPick(await onCreate(text));
    } catch {
      setCreateFailed(true);
    } finally {
      setCreating(false);
    }
  };

  const message = !results.ready ? (
    <ListMessage>{t('fields.selectTypeToSearch')}</ListMessage>
  ) : results.failed ? (
    <ListMessage>
      {t('fields.selectLoadFailed')}{' '}
      <button type="button" className="font-semibold text-text underline" onClick={results.retry}>
        {t('ui.retry')}
      </button>
    </ListMessage>
  ) : rows.length === 0 && !results.loading ? (
    <ListMessage>{t('fields.selectEmpty')}</ListMessage>
  ) : undefined;

  return (
    <>
      <ListPanel<AsyncRow<T>>
        items={rows}
        getKey={(row) => (row.kind === 'item' ? getKey(row.item) : `create:${row.query}`)}
        isSelected={(row) => row.kind === 'item' && getKey(row.item) === selectedKey}
        initialKey={selectedKey}
        label={label}
        search={{ value: query, onChange: onQueryChange }}
        loading={results.loading || creating}
        message={message}
        {...(renderPreview
          ? { renderPreview: (row: AsyncRow<T>) => (row.kind === 'item' ? renderPreview(row.item) : null) }
          : {})}
        onSelect={(row) => {
          if (row.kind === 'item') onPick(row.item);
          else void create(row.query);
        }}
        renderItem={(row, state) =>
          row.kind === 'item' ? (
            <OptionRow
              label={getLabel(row.item)}
              description={describe?.(row.item).description}
              icon={describe?.(row.item).icon}
              active={state.active}
              selected={state.selected}
            />
          ) : (
            <OptionRow
              label={t('fields.selectCreate', { query: row.query })}
              icon={<Plus />}
              active={state.active}
              selected={false}
            />
          )
        }
      />
      {createFailed ? (
        <p className="border-t border-border-soft px-2.5 py-2 text-caption font-semibold text-danger">
          {t('fields.selectCreateFailed')}
        </p>
      ) : null}
    </>
  );
}

export interface AsyncSelectFieldProps<T> extends FieldPart, Omit<AsyncSelectProps<T>, keyof FieldPart> {}

export function AsyncSelectField<T>(props: AsyncSelectFieldProps<T>) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <AsyncSelect {...input} />
    </Field>
  );
}
